import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnkaHeader } from "@/components/EnkaHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, USER_TOKEN_KEY } from "@/lib/api";
import { usePersonnel } from "@/lib/personnelContext";
import { Loader2, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function PersonnelLogin() {
  const navigate = useNavigate();
  const { refresh } = usePersonnel();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/personnel/login", {
        username: username.trim().toLowerCase(),
        pin: pin.trim(),
      });
      localStorage.setItem(USER_TOKEN_KEY, data.token);
      await refresh();
      toast.success(`Welcome, ${data.full_name}`);
      navigate("/my-tickets");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid username or PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="personnel-login-page">
      <EnkaHeader subtitle="Sign in to track your requests." />
      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <Card className="w-full max-w-md p-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-[color:var(--enka-blue-50)] text-[color:var(--enka-blue)] flex items-center justify-center">
              <LogIn className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl text-[color:var(--enka-ink)]">User Login</h1>
              <p className="text-xs text-[color:var(--enka-muted)]">Use your assigned username and 6-digit PIN.</p>
            </div>
          </div>
          <form className="space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="username" className="text-[color:var(--enka-ink)]">Username</Label>
              <Input
                id="username" data-testid="personnel-login-username"
                autoFocus autoComplete="username" autoCapitalize="none"
                value={username} onChange={(e) => setUsername(e.target.value)} required
                placeholder="e.g. ali.arif.ari"
              />
            </div>
            <div>
              <Label htmlFor="pin" className="text-[color:var(--enka-ink)]">6-digit PIN</Label>
              <Input
                id="pin" data-testid="personnel-login-pin"
                type="password" inputMode="numeric" pattern="[0-9]*"
                maxLength={6} autoComplete="one-time-code"
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} required
              />
            </div>
            <Button
              type="submit" disabled={loading || pin.length < 6}
              className="w-full h-11 bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
              data-testid="personnel-login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <div className="mt-5 pt-4 border-t border-border text-center">
            <button
              type="button" onClick={() => navigate("/")}
              className="inline-flex items-center gap-1 text-xs text-[color:var(--enka-muted)] hover:text-[color:var(--enka-blue)]"
              data-testid="personnel-login-guest-link"
            >
              <ArrowLeft className="h-3 w-3" /> Continue as guest
            </button>
          </div>
        </Card>
      </main>
    </div>
  );
}
